import React, { useState, useRef } from "react";
import { useSuppliers } from "../../hooks/useSuppliers";
import { useTranslation } from "react-i18next";

const UploadBulk = ({
  onNext,
  onCancel,
  selectedFile,
  setSelectedFile,
  selectedSupplier,
  setSelectedSupplier,
  ImagesZipFolder,
  setImagesZipFolder,
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [zipUploadProgress, setZipUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isZipDragging, setIsZipDragging] = useState(false);
  const fileInputRef = useRef(null);
  const zipFileInputRef = useRef(null);
  const { t } = useTranslation("bulkproducts");
  const {
    suppliers,
    loading: suppliersLoading,
    error: suppliersError,
  } = useSuppliers();
  const handleFileSelect = (file) => {
    setSelectedFile(file);
    // Simulate upload progress
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };

  const handleZipFileSelect = (file) => {
    setImagesZipFolder(file);
    // Simulate upload progress
    setZipUploadProgress(0);
    const interval = setInterval(() => {
      setZipUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleZipDragOver = (e) => {
    e.preventDefault();
    setIsZipDragging(true);
  };

  const handleZipDragLeave = (e) => {
    e.preventDefault();
    setIsZipDragging(false);
  };

  const handleZipDrop = (e) => {
    e.preventDefault();
    setIsZipDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleZipFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleZipFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleZipFileSelect(files[0]);
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  const handleClickZipUpload = () => {
    zipFileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveZipFile = () => {
    setImagesZipFolder(null);
    setZipUploadProgress(0);
    if (zipFileInputRef.current) {
      zipFileInputRef.current.value = "";
    }
  };

  const canProceed = selectedFile && selectedSupplier && uploadProgress === 100 && ImagesZipFolder && zipUploadProgress === 100;

  return (
    <div className="mx-auto p-6">
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          {t("upload.title")}
        </h2>
      </div>
      {/* File Upload Sections in Row */}
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Excel File Upload Area */}
          <div>
            <label className="block text-[16px] font-[600] text-[#464646] mb-3">
              {t("upload.title")} <span className="text-red-500">*</span>
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging
                  ? "border-purple-400 bg-purple-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="mb-4">
                <div className="w-10 h-10 mx-auto mb-4 text-purple-600">
                  <svg
                    width="44"
                    height="45"
                    viewBox="0 0 44 45"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      y="0.5"
                      width="44"
                      height="44"
                      rx="22"
                      fill="#014A6A"
                      fillOpacity="0.08"
                    />
                    <path
                      d="M18.9995 28.25C18.5895 28.25 18.2495 27.91 18.2495 27.5V23.31L17.5295 24.03C17.2395 24.32 16.7595 24.32 16.4695 24.03C16.1795 23.74 16.1795 23.26 16.4695 22.97L18.4695 20.97C18.6795 20.76 19.0095 20.69 19.2895 20.81C19.5695 20.92 19.7495 21.2 19.7495 21.5V27.5C19.7495 27.91 19.4095 28.25 18.9995 28.25Z"
                      fill="#6609F8"
                    />
                    <path
                      d="M20.9995 24.2499C20.8095 24.2499 20.6195 24.1799 20.4695 24.0299L18.4695 22.0299C18.1795 21.7399 18.1795 21.2599 18.4695 20.9699C18.7595 20.6799 19.2395 20.6799 19.5295 20.9699L21.5295 22.9699C21.8195 23.2599 21.8195 23.7399 21.5295 24.0299C21.3795 24.1799 21.1895 24.2499 20.9995 24.2499Z"
                      fill="#6609F8"
                    />
                    <path
                      d="M25 33.25H19C13.57 33.25 11.25 30.93 11.25 25.5V19.5C11.25 14.07 13.57 11.75 19 11.75H24C24.41 11.75 24.75 12.09 24.75 12.5C24.75 12.91 24.41 13.25 24 13.25H19C14.39 13.25 12.75 14.89 12.75 19.5V25.5C12.75 30.11 14.39 31.75 19 31.75H25C29.61 31.75 31.25 30.11 31.25 25.5V20.5C31.25 20.09 31.59 19.75 32 19.75C32.41 19.75 32.75 20.09 32.75 20.5V25.5C32.75 30.93 30.43 33.25 25 33.25Z"
                      fill="#6609F8"
                    />
                    <path
                      d="M32 21.25H28C24.58 21.25 23.25 19.92 23.25 16.5V12.5C23.25 12.2 23.43 11.92 23.71 11.81C23.99 11.69 24.31 11.76 24.53 11.97L32.53 19.97C32.74 20.18 32.81 20.51 32.69 20.79C32.57 21.07 32.3 21.25 32 21.25ZM24.75 14.31V16.5C24.75 19.08 25.42 19.75 28 19.75H30.19L24.75 14.31Z"
                      fill="#6609F8"
                    />
                  </svg>
                </div>
              </div>
              <p className="mb-2 text-sm">
                <button
                  onClick={handleClickUpload}
                  className="font-[600] text-[12px] hover:underline text-[#6609F8]"
                >
                  {t("upload.clickToUpload")}
                </button>{" "}
                {t("upload.orDrag")}
              </p>
              <p className="text-[10px] text-[#666666] font-[400]">
                {t("upload.maxFile")}
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileInputChange}
              accept=".xlsx,.xls"
              className="hidden"
            />
          </div>

          {/* ZIP File Upload Area */}
          <div>
            <label className="block text-[16px] font-[600] text-[#464646] mb-3">
              {t("upload.imagesZipLabel")} <span className="text-red-500">*</span>
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isZipDragging
                  ? "border-purple-400 bg-purple-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              onDragOver={handleZipDragOver}
              onDragLeave={handleZipDragLeave}
              onDrop={handleZipDrop}
            >
              <div className="mb-4">
                <div className="w-10 h-10 mx-auto mb-4 text-purple-600">
                  <svg
                    width="44"
                    height="45"
                    viewBox="0 0 44 45"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      y="0.5"
                      width="44"
                      height="44"
                      rx="22"
                      fill="#014A6A"
                      fillOpacity="0.08"
                    />
                    <path
                      d="M18.9995 28.25C18.5895 28.25 18.2495 27.91 18.2495 27.5V23.31L17.5295 24.03C17.2395 24.32 16.7595 24.32 16.4695 24.03C16.1795 23.74 16.1795 23.26 16.4695 22.97L18.4695 20.97C18.6795 20.76 19.0095 20.69 19.2895 20.81C19.5695 20.92 19.7495 21.2 19.7495 21.5V27.5C19.7495 27.91 19.4095 28.25 18.9995 28.25Z"
                      fill="#6609F8"
                    />
                    <path
                      d="M20.9995 24.2499C20.8095 24.2499 20.6195 24.1799 20.4695 24.0299L18.4695 22.0299C18.1795 21.7399 18.1795 21.2599 18.4695 20.9699C18.7595 20.6799 19.2395 20.6799 19.5295 20.9699L21.5295 22.9699C21.8195 23.2599 21.8195 23.7399 21.5295 24.0299C21.3795 24.1799 21.1895 24.2499 20.9995 24.2499Z"
                      fill="#6609F8"
                    />
                    <path
                      d="M25 33.25H19C13.57 33.25 11.25 30.93 11.25 25.5V19.5C11.25 14.07 13.57 11.75 19 11.75H24C24.41 11.75 24.75 12.09 24.75 12.5C24.75 12.91 24.41 13.25 24 13.25H19C14.39 13.25 12.75 14.89 12.75 19.5V25.5C12.75 30.11 14.39 31.75 19 31.75H25C29.61 31.75 31.25 30.11 31.25 25.5V20.5C31.25 20.09 31.59 19.75 32 19.75C32.41 19.75 32.75 20.09 32.75 20.5V25.5C32.75 30.93 30.43 33.25 25 33.25Z"
                      fill="#6609F8"
                    />
                    <path
                      d="M32 21.25H28C24.58 21.25 23.25 19.92 23.25 16.5V12.5C23.25 12.2 23.43 11.92 23.71 11.81C23.99 11.69 24.31 11.76 24.53 11.97L32.53 19.97C32.74 20.18 32.81 20.51 32.69 20.79C32.57 21.07 32.3 21.25 32 21.25ZM24.75 14.31V16.5C24.75 19.08 25.42 19.75 28 19.75H30.19L24.75 14.31Z"
                      fill="#6609F8"
                    />
                  </svg>
                </div>
              </div>
              <p className="mb-2 text-sm">
                <button
                  onClick={handleClickZipUpload}
                  className="font-[600] text-[12px] hover:underline text-[#6609F8]"
                >
                  {t("upload.clickToUploadZip")}
                </button>{" "}
                {t("upload.orDrag")}
              </p>
              <p className="text-[10px] text-[#666666] font-[400]">
                {t("upload.maxZipFile")}
              </p>
            </div>

            <input
              ref={zipFileInputRef}
              type="file"
              onChange={handleZipFileInputChange}
              accept=".zip"
              className="hidden"
            />
          </div>
        </div>

        {/* File Previews in Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Excel File Preview */}
          {selectedFile && (
            <div>
              <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 text-gray-600">
                    <svg
                      width="21"
                      height="21"
                      viewBox="0 0 21 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.9993 19.4582H7.99935C3.47435 19.4582 1.54102 17.5248 1.54102 12.9998V7.99984C1.54102 3.47484 3.47435 1.5415 7.99935 1.5415H12.166C12.5077 1.5415 12.791 1.82484 12.791 2.1665C12.791 2.50817 12.5077 2.7915 12.166 2.7915H7.99935C4.15768 2.7915 2.79102 4.15817 2.79102 7.99984V12.9998C2.79102 16.8415 4.15768 18.2082 7.99935 18.2082H12.9993C16.841 18.2082 18.2077 16.8415 18.2077 12.9998V8.83317C18.2077 8.4915 18.491 8.20817 18.8327 8.20817C19.1744 8.20817 19.4577 8.4915 19.4577 8.83317V12.9998C19.4577 17.5248 17.5243 19.4582 12.9993 19.4582Z"
                        fill="#666666"
                      />
                      <path
                        d="M18.8327 9.45841H15.4993C12.6493 9.45841 11.541 8.35007 11.541 5.50007V2.16674C11.541 1.91674 11.691 1.68341 11.9243 1.59174C12.1577 1.49174 12.4243 1.55007 12.6077 1.72507L19.2743 8.39174C19.4493 8.56674 19.5077 8.84174 19.4077 9.07507C19.3077 9.3084 19.0827 9.45841 18.8327 9.45841ZM12.791 3.67507V5.50007C12.791 7.65007 13.3493 8.20841 15.4993 8.20841H17.3243L12.791 3.67507Z"
                        fill="#666666"
                      />
                      <path
                        d="M11.334 11.9585H6.33398C5.99232 11.9585 5.70898 11.6752 5.70898 11.3335C5.70898 10.9918 5.99232 10.7085 6.33398 10.7085H11.334C11.6757 10.7085 11.959 10.9918 11.959 11.3335C11.959 11.6752 11.6757 11.9585 11.334 11.9585Z"
                        fill="#666666"
                      />
                      <path
                        d="M9.66732 15.2915H6.33398C5.99232 15.2915 5.70898 15.0082 5.70898 14.6665C5.70898 14.3248 5.99232 14.0415 6.33398 14.0415H9.66732C10.009 14.0415 10.2923 14.3248 10.2923 14.6665C10.2923 15.0082 10.009 15.2915 9.66732 15.2915Z"
                        fill="#666666"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedFile.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleRemoveFile}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    width="21"
                    height="21"
                    viewBox="0 0 21 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.9991 6.10839C17.9824 6.10839 17.9574 6.10839 17.9324 6.10839C13.5241 5.66673 9.12408 5.50006 4.76574 5.94173L3.06574 6.10839C2.71574 6.14173 2.40741 5.89173 2.37408 5.54173C2.34074 5.19173 2.59074 4.89173 2.93241 4.85839L4.63241 4.69173C9.06574 4.24173 13.5574 4.41673 18.0574 4.85839C18.3991 4.89173 18.6491 5.20006 18.6157 5.54173C18.5907 5.86673 18.3157 6.10839 17.9991 6.10839Z"
                      fill="#353535"
                    />
                    <path
                      d="M7.58265 5.2665C7.54932 5.2665 7.51598 5.2665 7.47432 5.25817C7.14098 5.19984 6.90765 4.87484 6.96598 4.5415L7.14932 3.44984C7.28265 2.64984 7.46598 1.5415 9.40765 1.5415H11.591C13.541 1.5415 13.7243 2.6915 13.8493 3.45817L14.0326 4.5415C14.091 4.88317 13.8576 5.20817 13.5243 5.25817C13.1826 5.3165 12.8576 5.08317 12.8076 4.74984L12.6243 3.6665C12.5076 2.9415 12.4826 2.79984 11.5993 2.79984H9.41598C8.53265 2.79984 8.51598 2.9165 8.39098 3.65817L8.19932 4.7415C8.14932 5.04984 7.88265 5.2665 7.58265 5.2665Z"
                      fill="#353535"
                    />
                    <path
                      d="M13.176 19.4582H7.82597C4.91764 19.4582 4.80097 17.8498 4.70931 16.5498L4.16764 8.15815C4.14264 7.81649 4.40931 7.51649 4.75097 7.49149C5.10097 7.47482 5.39264 7.73315 5.41764 8.07482L5.95931 16.4665C6.05097 17.7332 6.08431 18.2082 7.82597 18.2082H13.176C14.926 18.2082 14.9593 17.7332 15.0426 16.4665L15.5843 8.07482C15.6093 7.73315 15.9093 7.47482 16.251 7.49149C16.5926 7.51649 16.8593 7.80815 16.8343 8.15815L16.2926 16.5498C16.201 17.8498 16.0843 19.4582 13.176 19.4582Z"
                      fill="#353535"
                    />
                    <path
                      d="M11.8824 14.875H9.10742C8.76576 14.875 8.48242 14.5917 8.48242 14.25C8.48242 13.9083 8.76576 13.625 9.10742 13.625H11.8824C12.2241 13.625 12.5074 13.9083 12.5074 14.25C12.5074 14.5917 12.2241 14.875 11.8824 14.875Z"
                      fill="#353535"
                    />
                    <path
                      d="M12.5827 11.5415H8.41602C8.07435 11.5415 7.79102 11.2582 7.79102 10.9165C7.79102 10.5748 8.07435 10.2915 8.41602 10.2915H12.5827C12.9243 10.2915 13.2077 10.5748 13.2077 10.9165C13.2077 11.2582 12.9243 11.5415 12.5827 11.5415Z"
                      fill="#353535"
                    />
                  </svg>
                </button>
              </div>

              {/* Progress Bar */}
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#6609F8] h-[6px] rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-right text-sm text-gray-500 mt-1">
                  {uploadProgress}%
                </p>
              </div>
            </div>
          )}

          {/* ZIP File Preview */}
          {ImagesZipFolder && (
            <div>
              <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 text-gray-600">
                    <svg
                      width="21"
                      height="21"
                      viewBox="0 0 21 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.9993 19.4582H7.99935C3.47435 19.4582 1.54102 17.5248 1.54102 12.9998V7.99984C1.54102 3.47484 3.47435 1.5415 7.99935 1.5415H12.166C12.5077 1.5415 12.791 1.82484 12.791 2.1665C12.791 2.50817 12.5077 2.7915 12.166 2.7915H7.99935C4.15768 2.7915 2.79102 4.15817 2.79102 7.99984V12.9998C2.79102 16.8415 4.15768 18.2082 7.99935 18.2082H12.9993C16.841 18.2082 18.2077 16.8415 18.2077 12.9998V8.83317C18.2077 8.4915 18.491 8.20817 18.8327 8.20817C19.1744 8.20817 19.4577 8.4915 19.4577 8.83317V12.9998C19.4577 17.5248 17.5243 19.4582 12.9993 19.4582Z"
                        fill="#666666"
                      />
                      <path
                        d="M18.8327 9.45841H15.4993C12.6493 9.45841 11.541 8.35007 11.541 5.50007V2.16674C11.541 1.91674 11.691 1.68341 11.9243 1.59174C12.1577 1.49174 12.4243 1.55007 12.6077 1.72507L19.2743 8.39174C19.4493 8.56674 19.5077 8.84174 19.4077 9.07507C19.3077 9.3084 19.0827 9.45841 18.8327 9.45841ZM12.791 3.67507V5.50007C12.791 7.65007 13.3493 8.20841 15.4993 8.20841H17.3243L12.791 3.67507Z"
                        fill="#666666"
                      />
                      <path
                        d="M14.5827 15.2915H12.9993V13.7082H14.5827V15.2915ZM11.416 15.2915H9.83268V13.7082H11.416V15.2915ZM8.24935 15.2915H6.66602V13.7082H8.24935V15.2915Z"
                        fill="#666666"
                      />
                      <path
                        d="M14.5827 12.1248H12.9993V10.5415H14.5827V12.1248ZM11.416 12.1248H9.83268V10.5415H11.416V12.1248ZM8.24935 12.1248H6.66602V10.5415H8.24935V12.1248Z"
                        fill="#666666"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {ImagesZipFolder.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {(ImagesZipFolder.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleRemoveZipFile}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    width="21"
                    height="21"
                    viewBox="0 0 21 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.9991 6.10839C17.9824 6.10839 17.9574 6.10839 17.9324 6.10839C13.5241 5.66673 9.12408 5.50006 4.76574 5.94173L3.06574 6.10839C2.71574 6.14173 2.40741 5.89173 2.37408 5.54173C2.34074 5.19173 2.59074 4.89173 2.93241 4.85839L4.63241 4.69173C9.06574 4.24173 13.5574 4.41673 18.0574 4.85839C18.3991 4.89173 18.6491 5.20006 18.6157 5.54173C18.5907 5.86673 18.3157 6.10839 17.9991 6.10839Z"
                      fill="#353535"
                    />
                    <path
                      d="M7.58265 5.2665C7.54932 5.2665 7.51598 5.2665 7.47432 5.25817C7.14098 5.19984 6.90765 4.87484 6.96598 4.5415L7.14932 3.44984C7.28265 2.64984 7.46598 1.5415 9.40765 1.5415H11.591C13.541 1.5415 13.7243 2.6915 13.8493 3.45817L14.0326 4.5415C14.091 4.88317 13.8576 5.20817 13.5243 5.25817C13.1826 5.3165 12.8576 5.08317 12.8076 4.74984L12.6243 3.6665C12.5076 2.9415 12.4826 2.79984 11.5993 2.79984H9.41598C8.53265 2.79984 8.51598 2.9165 8.39098 3.65817L8.19932 4.7415C8.14932 5.04984 7.88265 5.2665 7.58265 5.2665Z"
                      fill="#353535"
                    />
                    <path
                      d="M13.176 19.4582H7.82597C4.91764 19.4582 4.80097 17.8498 4.70931 16.5498L4.16764 8.15815C4.14264 7.81649 4.40931 7.51649 4.75097 7.49149C5.10097 7.47482 5.39264 7.73315 5.41764 8.07482L5.95931 16.4665C6.05097 17.7332 6.08431 18.2082 7.82597 18.2082H13.176C14.926 18.2082 14.9593 17.7332 15.0426 16.4665L15.5843 8.07482C15.6093 7.73315 15.9093 7.47482 16.251 7.49149C16.5926 7.51649 16.8593 7.80815 16.8343 8.15815L16.2926 16.5498C16.201 17.8498 16.0843 19.4582 13.176 19.4582Z"
                      fill="#353535"
                    />
                    <path
                      d="M11.8824 14.875H9.10742C8.76576 14.875 8.48242 14.5917 8.48242 14.25C8.48242 13.9083 8.76576 13.625 9.10742 13.625H11.8824C12.2241 13.625 12.5074 13.9083 12.5074 14.25C12.5074 14.5917 12.2241 14.875 11.8824 14.875Z"
                      fill="#353535"
                    />
                    <path
                      d="M12.5827 11.5415H8.41602C8.07435 11.5415 7.79102 11.2582 7.79102 10.9165C7.79102 10.5748 8.07435 10.2915 8.41602 10.2915H12.5827C12.9243 10.2915 13.2077 10.5748 13.2077 10.9165C13.2077 11.2582 12.9243 11.5415 12.5827 11.5415Z"
                      fill="#353535"
                    />
                  </svg>
                </button>
              </div>

              {/* Progress Bar */}
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#6609F8] h-[6px] rounded-full transition-all duration-300"
                    style={{ width: `${zipUploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-right text-sm text-gray-500 mt-1">
                  {zipUploadProgress}%
                </p>
                </div>
              </div>
            )}
          </div>

        {/* Supplier Selection */}
        <div className="w-[405px] mx-auto mb-8">
          <label className="block text-[16px] font-[600] text-[#464646] mb-2">
            {t("upload.selectSupplier")} <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedSupplier}
            onChange={(e) => setSelectedSupplier(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none font-[500] text-[16px] "
          >
            <option value="">{t("upload.selectSupplier")}</option>
           {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="w-[163px] h-[43px] leading-[1] px-[8px] py-[12px] border border-[#292929] text-[#292929] font-[400] text-[16px] rounded-[8px] hover:bg-gray-50 transition-colors"
        >
          {t("upload.cancel")}
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className={`w-[260px] h-[43px] px-[8px] py-[12px] leading-[1] bg-[#6609F8]  text-[#F4F1FF] opacity-${canProceed ? "100" : "50"} font-[400] text-[16px]
          rounded-[8px] hover:bg-[#5b08e0] transition-colors`}
        >
          {t("upload.next")}
        </button>
      </div>
    </div>
  );
};

export default UploadBulk;
